import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";

import {
  getCashierConfig,
  getPaymentMethodsList,
  handleSetActiveCashierGeo,
  handleSetActiveCashierCurrency,
} from "store/players/presets/slice";

import RoundBtn from "components/buttons/RoundBtn";

import MainPageDragItem from "pages/CMS/Widgets/components/MainPageDragItem";
import MainPageSortItem from "pages/CMS/Widgets/components/MainPageSortItem";

import "pages/CMS/Widgets/Widgets.scss";
import { Modal } from "components/Modal/Modal";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import Tooltip from "components/Tooltip";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import SwitchBtn from "components/buttons/SwitchBtn";
import { getFieldErrorKeys } from "helpers/getFieldErrorKeys";

interface ICashierItem {
  id: number;
  label: string;
  image?: string;
  groupKey?: "fiat" | "crypto";
}

interface Props {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  resetErrors: () => void;
  onChange: (payload: any) => void;
  readOnly?: boolean;
}

const PresetsCashierTab: React.FC<Props> = ({
  onChange,
  readOnly,
  fieldErrors,
  clearFieldError,
  resetErrors,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const presetId = searchParams.get("entityId");

  const geoTabs = useAppSelector((state) => state.settings.geoList);
  const currenciesList = useAppSelector((state) => state.settings.currencies);

  const [activeGeo, setActiveGeo] = useState("WW");
  const [activeCurrency, setActiveCurrency] = useState("EUR");

  const [selectedItems, setSelectedItems] = useState<ICashierItem[]>([]);

  const { cashierConfig, availablePaymentMethods, isCashierFetching } =
    useAppSelector((state) => state.presets);

  const [selectedToAdd, setSelectedToAdd] = useState<ICashierItem[]>([]);

  const [selectedToDelete, setSelectedToDelete] = useState<ICashierItem[]>([]);
  const [currenciesConfig, setCurrenciesConfig] = useState<
    Record<
      string,
      {
        defaultAmount: string;
        suggestedAmounts: string[];
      }
    >
  >({});
  const [showCashierAfterRegistration, setShowCashierAfterRegistration] =
    useState<boolean>(false);

  useEffect(() => {
    if (!cashierConfig?.paymentGroups?.length) {
      setSelectedItems([]);

      return;
    }

    const mappedSelectedItems = cashierConfig.paymentGroups.flatMap(
      (group) =>
        group.methodIds
          .map((methodId) => {
            const method = availablePaymentMethods.find(
              (item) => item.id === methodId,
            );

            if (!method) {
              return null;
            }

            return {
              id: method.id,
              label: method.title,
              groupKey: group.groupKey,
              img: method.image,
            };
          })
          .filter(Boolean) as ICashierItem[],
    );

    setSelectedItems(mappedSelectedItems);
  }, [cashierConfig, availablePaymentMethods]);

  useEffect(() => {
    if (!currenciesConfig[activeCurrency]) {
      setCurrenciesConfig((prev) => ({
        ...prev,
        [activeCurrency]: {
          defaultAmount: "",
          suggestedAmounts: ["", "", "", ""],
        },
      }));
    }
  }, [activeCurrency, currenciesConfig]);

  useEffect(() => {
    if (!cashierConfig?.currencies?.length) {
      setCurrenciesConfig({
        EUR: {
          defaultAmount: "",
          suggestedAmounts: ["", "", "", ""],
        },
      });

      return;
    }

    const mappedConfig = cashierConfig.currencies.reduce(
      (acc, currencyItem) => {
       acc[currencyItem.currency] = {
         defaultAmount: String(currencyItem.defaultAmount),
         suggestedAmounts: [
           ...currencyItem.suggestedAmounts.map(String),
           "",
           "",
           "",
           "",
         ].slice(0, 4),
       };

        return acc;
      },
      {} as Record<
        string,
        {
          defaultAmount: string;
          suggestedAmounts: string[];
        }
      >,
    );

    setCurrenciesConfig(mappedConfig);
    setShowCashierAfterRegistration(
      cashierConfig?.showCashierAfterRegistration ?? false,
    );
  }, [cashierConfig]);

  useEffect(() => {
    if (!presetId) {
      return;
    }

    dispatch(
      getCashierConfig({
        id: presetId,
        geo: activeGeo,
      }),
    );

    dispatch(
      getPaymentMethodsList({
        geo: activeGeo,
        currency: activeCurrency,
      }),
    );
  }, [dispatch, presetId, activeGeo, activeCurrency]);

  const mappedAvailableItems: ICashierItem[] = availablePaymentMethods.map(
    (el) => ({
      id: el.id,
      label: el.title,
      groupKey: el.currencyType,
      img: el.image,
    }),
  );


  const buildCashierPayload = () => {
    return {
      showCashierAfterRegistration,
      geoConfigs: [
        {
          geo: activeGeo,

          currencies: Object.entries(currenciesConfig).map(
            ([currency, config]) => ({
              currency,
              defaultAmount: Number(config.defaultAmount),
              suggestedAmounts: config.suggestedAmounts
                .filter((amount) => amount !== "")
                .map(Number),
            }),
          ),

          paymentGroups: [
            {
              groupKey: "fiat",
              methodIds: selectedItems
                .filter((item) => item.groupKey === "fiat")
                .map((item) => item.id),
            },

            {
              groupKey: "crypto",
              methodIds: selectedItems
                .filter((item) => item.groupKey === "crypto")
                .map((item) => item.id),
            },
          ],
        },
      ],

      isActive: 1,
    };
  };

  const currencyIndex = Object.keys(currenciesConfig).indexOf(activeCurrency);
  const currencyPath = `geoConfigs.0.currencies.${currencyIndex}`;
  const defaultErrors = getFieldErrorKeys(
    fieldErrors,
    "defaultAmount",
    `${currencyPath}.defaultAmount`,
  );
  const suggestedErrors = (index: number) => {
    const amounts = currenciesConfig[activeCurrency]?.suggestedAmounts ?? [];
    const requestIndex = amounts.slice(0, index)
      .filter((amount) => amount !== "").length;
    return getFieldErrorKeys(
      fieldErrors,
      "suggestedAmounts",
      `${currencyPath}.suggestedAmounts`,
      ...(amounts[index] !== "" ? [
        `suggestedAmounts.${requestIndex}`,
        `${currencyPath}.suggestedAmounts.${requestIndex}`,
      ] : []),
    );
  };

  useEffect(() => {
    onChange(buildCashierPayload());
  }, [selectedItems, currenciesConfig, showCashierAfterRegistration]);

  return (
    <div className="preset-form-wrapper preset-cashier-tab-wrapper">
      <div className="preset-form-content preset-cashier-tab-wrapper">
        <div className="tabsGeo">
          <Modal.Tabs
            tabs={geoTabs}
            activeTab={activeGeo}
            onClick={(geo) => {
              resetErrors();
              setActiveGeo(geo);
              dispatch(handleSetActiveCashierGeo(geo));
            }}
            isGeoTabs={true}
            isSmallModifier={true}
          />
        </div>
        <div className="tabsGeo">
          <Modal.Tabs
            tabs={currenciesList}
            activeTab={activeCurrency}
            onClick={(currency) => {
              setActiveCurrency(currency);
              dispatch(handleSetActiveCashierCurrency(currency));
            }}
            isCurrencyTabs
            isSmallModifier={true}
          />
        </div>
        {isCashierFetching ? (
          <ModalTabSkeleton />
        ) : (
          <>
            <div className="preset-cashier-amounts">
              <div className="preset-cashier-amounts__default">
                <InnerInput
                  id="default-amount"
                  hasError={defaultErrors.length > 0}
                  type="number"
                  label={t("defaultAmounts")}
                  value={currenciesConfig[activeCurrency]?.defaultAmount ?? ""}
                  onChange={(value) => {
                    defaultErrors.forEach(clearFieldError);
                    setCurrenciesConfig((prev) => ({
                      ...prev,
                      [activeCurrency]: {
                        ...prev[activeCurrency],
                        defaultAmount: value,
                        suggestedAmounts:
                          prev[activeCurrency]?.suggestedAmounts ?? [],
                      },
                    }));
                  }}
                  tooltipText={t("defaultDepositAmountForSelectedCurrency")}
                  tooltipPosition="top"
                  disabled={readOnly}
                />
              </div>

              <div className="preset-cashier-amounts__suggested">
                <div className="preset-cashier-amounts__title">
                  {t("suggestedAmounts")}

                  <Tooltip
                    text={t("quickAmountButtonsShownToPlayer")}
                    position="top"
                  >
                    <span className="info-icon">ℹ</span>
                  </Tooltip>
                </div>

                <div className="preset-cashier-amounts__row">
                  {(
                    currenciesConfig[activeCurrency]?.suggestedAmounts ?? []
                  ).map((amount, index) => (
                    <div key={index} className="preset-cashier-amounts__item">
                      <InnerInput
                        id={`suggested-${index}`}
                        hasError={suggestedErrors(index).length > 0}
                        type="number"
                        value={amount}
                        onChange={(value) => {
                          (currenciesConfig[activeCurrency]?.suggestedAmounts ?? [])
                            .flatMap((_, slot) => suggestedErrors(slot))
                            .forEach(clearFieldError);
                          const updated = [
                            ...(currenciesConfig[activeCurrency]
                              ?.suggestedAmounts ?? []),
                          ];

                          updated[index] = value;

                          setCurrenciesConfig((prev) => ({
                            ...prev,
                            [activeCurrency]: {
                              ...prev[activeCurrency],
                              defaultAmount:
                                prev[activeCurrency]?.defaultAmount ?? "",
                              suggestedAmounts: updated,
                            },
                          }));
                        }}
                        disabled={readOnly}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="preset-cashier-amounts__toggle">
                <div className="preset-cashier-amounts__title">
                  {t("showAfterRegistration")}
                </div>

                <SwitchBtn
                  activeTxt=""
                  notActiveTxt=""
                  isActive={showCashierAfterRegistration}
                  handleClick={() =>
                    setShowCashierAfterRegistration((prev) => !prev)
                  }
                  disabled={readOnly}
                />
              </div>
            </div>
            <div className="main-page-wrapper preset-cashier-widgets-wrapper">
              <div className="main-page-content">
                <div className="main-page-widget">
                  <div className="main-page-widget--box">
                    <div className="main-page-title">{t("selectedItems")}</div>

                    <div className="main-page-widget--list">
                      {selectedItems.map((el, index) => (
                        <MainPageSortItem
                          key={el.id}
                          item={el}
                          index={index}
                          moveItem={() => {}}
                          isSelected={selectedToDelete.some(
                            (item) => item.id === el.id,
                          )}
                          handleClick={() => {
                            if (
                              selectedToDelete.some((item) => item.id === el.id)
                            ) {
                              setSelectedToDelete(
                                selectedToDelete.filter(
                                  (item) => item.id !== el.id,
                                ),
                              );
                            } else {
                              setSelectedToDelete([...selectedToDelete, el]);
                            }
                          }}
                          isBurger
                          customContent={
                            el.groupKey ? (
                              <div className={`type-label ${el.groupKey}`}>
                                {el.groupKey}
                              </div>
                            ) : null
                          }
                          isDraggable={!readOnly}
                          readOnly={readOnly}
                        />
                      ))}
                    </div>

                    <div className="main-page-btns">
                        {!readOnly && (  
                        <>
                          <RoundBtn
                            txt="+"
                            disabled={selectedToAdd.length === 0}
                            handleClick={() => {
                              setSelectedItems([
                                ...selectedItems,
                                ...selectedToAdd,
                              ]);

                              setSelectedToAdd([]);
                            }}
                          />

                          <RoundBtn
                            txt="-"
                            disabled={selectedToDelete.length === 0}
                            handleClick={() => {
                              setSelectedItems(
                                selectedItems.filter(
                                  (item) =>
                                    !selectedToDelete.find(
                                      (selected) => selected.id === item.id,
                                    ),
                                ),
                              );

                              setSelectedToDelete([]);
                            }}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="main-page-widget">
                  <div className="main-page-widget--box">
                    <div className="main-page-title">{t("availableItems")}</div>

                    <div className="main-page-list-content">
                      {mappedAvailableItems
                        .filter(
                          (item) =>
                            !selectedItems.find(
                              (selected) => selected.id === item.id,
                            ),
                        )
                        .map((el) => (
                          <MainPageDragItem
                            key={el.id}
                            item={el}
                            type="cashier"
                            isSelected={selectedToAdd.some(
                              (item) => item.id === el.id,
                            )}
                            handleClick={() => {
                              if (
                                selectedToAdd.some((item) => item.id === el.id)
                              ) {
                                setSelectedToAdd(
                                  selectedToAdd.filter(
                                    (item) => item.id !== el.id,
                                  ),
                                );
                              } else {
                                setSelectedToAdd([...selectedToAdd, el]);
                              }
                            }}
                            isBurger
                            customContent={
                              el.groupKey ? (
                                <div className={`type-label ${el.groupKey}`}>
                                  {el.groupKey}
                                </div>
                              ) : null
                            }
                          />
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PresetsCashierTab;
